// Columns of a seat-holder's passenger table on the ShareJet booking page.
import { Button, Popconfirm, Space, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';

import type { AdminBookingPassenger } from '../../api/types';
import { dobColumn, expiryColumn, genderColumn, issuerColumn, nationalityColumn } from '../../components/passengerColumns';
import { CopyPassengerButton } from '../../components/CopyPassengerButton';
import { DocThumbs } from '../../components/passengerBits';

const { Text } = Typography;

export function passengerTableColumns({
  openEdit,
  deleteMut,
  lockedReason,
}: {
  openEdit: (p: AdminBookingPassenger) => void;
  deleteMut: { mutate: (pid: string) => void };
  /** Admin regression 6 Oct — set on a cancelled booking: edit and remove are disabled, with this tooltip. */
  lockedReason?: string;
}): ColumnsType<AdminBookingPassenger> {
  return [
    { title: 'Name', dataIndex: 'fullName', key: 'fullName' },
    // SHAR-1064 — the same identity columns as the Charter booking table.
    dobColumn<AdminBookingPassenger>(),
    genderColumn<AdminBookingPassenger>(),
    nationalityColumn<AdminBookingPassenger>(),
    {
      title: 'Passport no.',
      key: 'pp',
      render: (_, r) =>
        r.passportPending ? <Tag>To be provided</Tag> : r.passportNumber || '—',
    },
    issuerColumn<AdminBookingPassenger>(),
    expiryColumn<AdminBookingPassenger>(),
    {
      title: 'Documents',
      key: 'docs',
      render: (_, r) => {
        const links: { url: string; label: string }[] = [];
        if (r.passportPhotoUrl)
          links.push({ url: r.passportPhotoUrl, label: 'Passport' });
        r.extraDocUrls.forEach((u, i) => links.push({ url: u, label: `Doc ${i + 1}` }));
        if (!links.length) return <Text type="secondary">—</Text>;
        // SHAR-609 — thumbnails rather than text links, because an
        // admin checking a passport wants to see it, not read the
        // word "Passport". Click opens full size.
        return <DocThumbs docs={links} />;
      },
    },
    {
      title: '',
      key: 'act',
      width: 130,
      render: (_, r) => (
        <Space>
          {/* SHAR-127 — copy this passenger's details. */}
          <CopyPassengerButton passenger={r} />
          <Tooltip title={lockedReason ?? ''}>
            <Button size="small" icon={<EditOutlined />} disabled={!!lockedReason} onClick={() => openEdit(r)} />
          </Tooltip>
          <Popconfirm
            title="Remove this passenger?"
            okText="Remove"
            okButtonProps={{ danger: true }}
            disabled={!!lockedReason}
            onConfirm={() => deleteMut.mutate(r.id)}
          >
            <Tooltip title={lockedReason ?? ''}>
              <Button size="small" danger icon={<DeleteOutlined />} disabled={!!lockedReason} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];
}
